import { ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatOption } from '@angular/material/autocomplete';
import { FormControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AutoFill } from '@galaxy/crm/components/animations';
import { FieldValueInterface, FormInputComponent, ObjectType } from '../../../../tokens-and-interfaces';
import { InlineEditService, UpdaterService } from '../..';
import { CrmFieldService, CrmObjectService, CrmPipelineService, Stage, StandardIds } from '../../../../shared-services';
import { map, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

@Component({
  templateUrl: './stage-selector.component.html',
  styleUrls: ['../renderers.scss'],
  imports: [CommonModule, MatSelectModule, GalaxyFormFieldModule, TranslateModule, MatOption, ReactiveFormsModule],
  animations: [AutoFill],
})
export class StageSelectorComponent extends FormInputComponent {
  private readonly pipelineService = inject(CrmPipelineService);
  private readonly updaterService = inject(UpdaterService);
  private readonly fieldService = inject(CrmFieldService);
  private readonly crmObjectService = inject(CrmObjectService);
  private readonly changeDetRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly inlineEditService = inject(InlineEditService);
  override objectType: ObjectType = 'Opportunity';

  _fieldId = '';
  set fieldId(fieldId: string) {
    this._fieldId = fieldId;
  }
  get fieldId(): string {
    return this._fieldId;
  }
  protected readonly selectedStage = signal('');
  private readonly selectedMutations = computed(() => {
    const newStageId = this.selectedStage();
    const stageObject = this.availableStageOptions().find((stage) => stage.id === newStageId);
    return stageObject?.mutations || [];
  });
  _control: FormControl<string> = new FormControl('', {
    nonNullable: true,
  });
  set control(control: FormControl<string>) {
    this._control = control;
    //parent component will override control so we need to track changes through a seperate object
    this._control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      this.selectedStage.set(val);
    });
  }
  get control(): UntypedFormControl {
    return this._control;
  }

  _label = '';
  set label(label: string) {
    this._label = label;
  }
  get label(): string {
    return this._label;
  }

  autoFillState = signal<string>('default');
  private readonly objectId$ = toObservable(this.objectId);
  private readonly currentPipelineId = signal('');
  private readonly stageOptionsPerPipeline = toSignal(
    this.pipelineService.listPipelines$(this.objectType).pipe(
      map((pipelines) => {
        const pipelineMap = new Map<string, Stage[]>();
        pipelines.forEach((pipeline) => {
          pipelineMap.set(pipeline.id, pipeline.stages);
        });
        return pipelineMap;
      }),
    ),
  );
  protected readonly availableStageOptions = computed(() => {
    const allOptions = this.stageOptionsPerPipeline();
    const selectedPipeline = this.currentPipelineId();
    return allOptions?.get(selectedPipeline) || [];
  });
  private readonly existingObject = toSignal(
    this.objectId$.pipe(
      switchMap((objectId) => this.crmObjectService.getMultiObject(this.objectType, [objectId])),
      map((resp) => (resp.crmObjects && resp.crmObjects.length > 0 ? resp.crmObjects[0] : {})),
    ),
  );
  protected readonly existingStage = computed(() => {
    const existingObject = this.existingObject();
    if (existingObject) {
      const fieldValue = this.fieldService.getFieldValueFromCrmObject(
        existingObject,
        StandardIds.OpportunityCalculatedStageID,
      );
      return fieldValue?.stringValue || '';
    }
    return '';
  });

  constructor() {
    super();
    effect(() => {
      if (!this.objectId()) {
        this.setInitialStage();
      } else {
        const existingStage = this.existingStage();
        if (existingStage) {
          this._control.setValue(existingStage, { emitEvent: false });
        }
      }
    });

    this.updaterService.updatedField.pipe(takeUntilDestroyed()).subscribe((fieldChanged) => {
      //watch for pipeline changes
      if (
        fieldChanged?.fieldId === StandardIds.OpportunityPipelineID &&
        fieldChanged?.stringValue !== this.currentPipelineId()
      ) {
        this.currentPipelineId.set(fieldChanged?.stringValue || '');
        const allStages = this.stageOptionsPerPipeline();
        const validStages = allStages?.get(fieldChanged?.stringValue ?? '') ?? [];
        this.control.setValue(validStages.length > 0 ? validStages[0].id : '');
        this.control.markAsDirty();
        this.highlightField();
      }
    });
  }

  set formGroup(group: UntypedFormGroup) {
    this.currentPipelineId.set(group?.value[StandardIds.OpportunityPipelineID] || '');
  }

  get hasModification(): boolean {
    return this.control.dirty;
  }

  get value(): FieldValueInterface {
    const stageMutations = this.selectedMutations();
    const probability = stageMutations.find((stage) => stage.fieldId === StandardIds.OpportunityProbability);
    return {
      fieldId: this.fieldId,
      floatValue: probability?.floatValue,
    };
  }

  private setInitialStage(): void {
    const allStages = this.stageOptionsPerPipeline();
    const validStages = allStages?.get(this.currentPipelineId() ?? '') ?? [];
    this._control.setValue(validStages.length > 0 ? validStages[0].id : '');
  }

  saveField(): void {
    const stageId = this.selectedStage();
    const stageMutations = this.selectedMutations();
    if (stageMutations?.length > 0) {
      for (const mutation of stageMutations) {
        this.updaterService.updateFieldValue(mutation);
      }
      this.updaterService.updateStringField(this.fieldId, stageId);
      this.inlineEditService.saveInlineEdit(this.objectType, stageMutations);
    }
  }

  private highlightField(): void {
    this.autoFillState.set('highlight');
    setTimeout(() => {
      this.autoFillState.set('default');
      this.changeDetRef.detectChanges();
    }, 1200);
  }
}
